import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  WorkflowAutoCreationDayFilter,
  WorkflowAutoCreationLeadUnit,
  WorkflowPlanAutomation,
} from '@ebalance/proto/bffuser/v1/workflow_auto_creation_rule_model_pb'
import type { TFunction } from 'i18next'
import { z } from 'zod'

import {
  type AutoCreationRuleRow,
  parseTimeOfDayHm,
} from '~/pages/AdminWorkflowTemplates/models/autoCreationRule'
import { effectivePlanAutomation } from '~/pages/AdminWorkflowTemplates/models/planAutomation'

const V = 'page:adminWorkflowTemplates.autoCreationRule.validation'

/** 新規の起票リードの上限 (日)。spec §4 / PdM確認 #12430。 */
export const MAX_LEAD_DAYS = 30

export type AutoCreationRuleFormValues = {
  templateId: string
  areaCodes: AreaCode[]
  /** 入力欄の文字列のまま持ち、送信時に数値にする。 */
  leadValue: string
  leadUnit: WorkflowAutoCreationLeadUnit
  /** `HH:mm` (JST)。 */
  fireTimeHm: string
  dayFilter: WorkflowAutoCreationDayFilter
  /** 計画の自動化 (AC-WFT-043)。 */
  planAutomation: WorkflowPlanAutomation
  enabled: boolean
}

/**
 * 新規作成の保存前の検査 (AC-WFT-052)。起票リードは 0〜30 の整数で、単位は日だけ
 * (月・年は AC-WFT-051 まで選べない)。
 */
export function createAutoCreationRuleFormSchema(
  t: TFunction,
): z.ZodType<AutoCreationRuleFormValues, AutoCreationRuleFormValues> {
  return z.object({
    templateId: z.string().min(1, t(`${V}.templateRequired`)),
    areaCodes: z.array(z.nativeEnum(AreaCode)).min(1, t(`${V}.areaRequired`)),
    leadValue: z
      .string()
      .refine(
        (v) => /^\d+$/.test(v.trim()) && Number(v.trim()) <= MAX_LEAD_DAYS,
        t(`${V}.leadInvalid`),
      ),
    leadUnit: z.literal(WorkflowAutoCreationLeadUnit.DAY, {
      message: t(`${V}.leadInvalid`),
    }),
    fireTimeHm: fireTimeHmSchema(t),
    dayFilter: z.nativeEnum(WorkflowAutoCreationDayFilter),
    planAutomation: z.nativeEnum(WorkflowPlanAutomation),
    enabled: z.boolean(),
  })
}

/**
 * 編集の保存前の検査。雛形・エリア・起票リードは編集で変わらず form の外の行の値
 * を送るので、検査するのは起票時刻・曜日・計画の自動化・有効だけ (範囲外の既存行も保存できる)。
 */
export function editAutoCreationRuleFormSchema(
  t: TFunction,
): z.ZodType<AutoCreationRuleFormValues, AutoCreationRuleFormValues> {
  return z.object({
    templateId: z.string(),
    areaCodes: z.array(z.nativeEnum(AreaCode)),
    leadValue: z.string(),
    leadUnit: z.nativeEnum(WorkflowAutoCreationLeadUnit),
    fireTimeHm: fireTimeHmSchema(t),
    dayFilter: z.nativeEnum(WorkflowAutoCreationDayFilter),
    planAutomation: z.nativeEnum(WorkflowPlanAutomation),
    enabled: z.boolean(),
  })
}

function fireTimeHmSchema(t: TFunction): z.ZodString {
  return z
    .string()
    .refine(
      (v) => parseTimeOfDayHm(v) !== undefined,
      t(`${V}.fireTimeRequired`),
    )
}

/**
 * 新規の既定値 (AC-WFT-021)。起票リードは雛形を選ぶまでの仮の値で、雛形を選ぶと
 * `defaultLeadForSection` の値に入れ替える。
 */
export function defaultAutoCreationRuleFormValues(): AutoCreationRuleFormValues {
  return {
    templateId: '',
    areaCodes: [],
    leadValue: '1',
    leadUnit: WorkflowAutoCreationLeadUnit.DAY,
    fireTimeHm: '00:00',
    dayFilter: WorkflowAutoCreationDayFilter.EVERY_DAY,
    planAutomation: WorkflowPlanAutomation.SAME_AS_TEMPLATE,
    enabled: true,
  }
}

export function autoCreationRuleFormValuesFromRow(
  row: AutoCreationRuleRow,
): AutoCreationRuleFormValues {
  return {
    templateId: row.template.id,
    areaCodes: [row.areaCode],
    leadValue: String(row.schedule.leadValue),
    leadUnit: row.schedule.leadUnit,
    fireTimeHm: row.schedule.fireTimeHm,
    dayFilter: row.schedule.dayFilter,
    planAutomation: effectivePlanAutomation(row.schedule.planAutomation),
    enabled: row.enabled,
  }
}
